import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

// =====================================================
// TYPES
// =====================================================

export interface WebhookLog {
  id: string;
  webhook_id: string;
  response_id: string | null;
  status: 'pending' | 'success' | 'failed';
  attempt_number: number;
  request_payload: Record<string, any> | null;
  response_status_code: number | null;
  response_body: string | null;
  error_message: string | null;
  event_type: string | null;
  created_at: string;
  // Joined from form_webhooks
  webhook_name?: string;
}

export interface WebhookLogFilter {
  status?: 'pending' | 'success' | 'failed' | 'all';
  webhookId?: string;
  eventType?: string;
  dateFrom?: string;
  dateTo?: string;
}

// =====================================================
// HOOK
// =====================================================

const PAGE_SIZE = 50;

export function useWebhookLogs(formId: string | undefined, filter?: WebhookLogFilter) {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchLogs = useCallback(async () => {
    if (!formId) {
      setLogs([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First get webhook IDs for this form
      const { data: webhooks, error: webhooksError } = await supabase
        .from('form_webhooks')
        .select('id, name')
        .eq('form_id', formId);

      if (webhooksError || !webhooks?.length) {
        setLogs([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      const webhookIds = webhooks.map(w => w.id);
      const webhookNameMap = new Map(webhooks.map(w => [w.id, w.name]));

      let query = supabase
        .from('webhook_logs')
        .select('*', { count: 'exact' })
        .in('webhook_id', webhookIds)
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (filter?.status && filter.status !== 'all') {
        query = query.eq('status', filter.status);
      }

      if (filter?.webhookId) {
        query = query.eq('webhook_id', filter.webhookId);
      }

      if (filter?.eventType && filter.eventType !== 'all') {
        query = query.eq('event_type', filter.eventType);
      }

      if (filter?.dateFrom) {
        query = query.gte('created_at', filter.dateFrom);
      }

      if (filter?.dateTo) {
        query = query.lte('created_at', filter.dateTo);
      }

      const { data, error: logsError, count } = await query;

      if (logsError) {
        console.error('Fetch webhook logs error:', logsError);
        setError('Erro ao carregar logs de webhooks');
        return;
      }

      const enrichedLogs = (data || []).map(log => ({
        ...log,
        webhook_name: webhookNameMap.get(log.webhook_id) || 'Webhook',
      })) as WebhookLog[];

      setLogs(enrichedLogs);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Fetch webhook logs error:', err);
      setError('Erro inesperado ao carregar logs');
    } finally {
      setLoading(false);
    }
  }, [formId, filter?.status, filter?.webhookId, filter?.eventType, filter?.dateFrom, filter?.dateTo, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh: poll every 10s when there are pending logs
  useEffect(() => {
    const hasPending = logs.some(l => l.status === 'pending');

    if (hasPending) {
      pollIntervalRef.current = setInterval(fetchLogs, 10000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [logs, fetchLogs]);

  return {
    logs,
    loading,
    error,
    totalCount,
    page,
    pageSize: PAGE_SIZE,
    setPage,
    refetch: fetchLogs,
  };
}
